import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authClient } from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';

interface AuthData {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
}
interface AuthState {
    data: AuthData | null;
    loading?: boolean;
    error?: boolean;
}

const initialState: AuthState = {
    data: null,
    loading: false,
    error: false,
};

type UserData = {
    [key: string]: number | undefined;
};
const totalAttempts = 10;
const updateUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON) || {};
    }
    if (userData?.[userId]) {
        userData[userId] = (userData?.[userId] || 0) + 1;
    } else {
        userData[userId] = 1;
    }
    const attempts = totalAttempts - (userData?.[userId] || 1);
    if (attempts > 0) {
        notify(`You have ${totalAttempts - (userData?.[userId] || 1)} attempts left! After that your account will be locked.`, 'error');
        localStorage.setItem('userAttempts', JSON.stringify(userData));
    }
};

const removeUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON);
    }
    if (!userData?.[userId]) return;

    delete userData[userId];
    localStorage.setItem('userAttempts', JSON.stringify(userData));
};

interface LoginPayload {
    email: string;
    password: string;
    deviceId?: string;
}

interface RegisterPayload {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
}

interface VerifyEmailPinPayload {
    email: string;
    pin: string;
}

interface ResendVerificationPayload {
    email: string;
}

interface Request2FAPinPayload {
    pendingToken: string;
    method: string;
}

interface Verify2FAPayload {
    pendingToken: string;
    pin: string;
    deviceId?: string;
    deviceName?: string;
}

interface RequestPasswordResetPayload {
    email: string;
}

interface VerifyResetPinPayload {
    email: string;
    pin: string;
}

interface VerifyResetPinResult {
    resetToken: string;
    expiresIn: number;
}

interface ResetPasswordPayload {
    resetToken: string;
    newPassword: string;
}

interface ChangePasswordPayload {
    currentPassword: string;
    newPassword: string;
}

export const login = createAsyncThunkWrapper<any, LoginPayload>('auth/login', async ({ email, password, deviceId }: LoginPayload) => {
    try {
        const response: any = await authClient.post('auth/login', { email, password, deviceId });
        if (response?.challenge === '2FA') {
            // Not an error - the caller (Login.tsx) reads this fulfilled payload to navigate
            // to the 2FA pin screen instead of treating it as a successful login.
            return { challenge: '2FA', methods: response.methods, pendingToken: response.pendingToken, email };
        }
        removeUserAttempts(email);
        authClient.setToken(response.accessToken, response.refreshToken);
        localStorage.setItem('token', JSON.stringify(response));
        return response;
    } catch (error: any) {
        if (error?.response?.status === 403 && /not verified/i.test(error?.response?.data?.detail || '')) {
            // Same idea: a fulfilled payload the caller checks, not a rejection, so Login.tsx
            // can route to the verify-email screen instead of just showing a generic error.
            return { emailNotVerified: true, email };
        }
        updateUserAttempts(email);
        throw error;
    }
});

export const register = createAsyncThunkWrapper<any, RegisterPayload>(
    'auth/register',
    async ({ email, password, firstName, lastName }: RegisterPayload) => {
        const res: any = await authClient.post('auth/register', {
            email,
            password,
            firstName,
            lastName,
        });
        return res;
    }
);

export const requestPasswordReset = createAsyncThunkWrapper<any, RequestPasswordResetPayload>(
    'auth/requestPasswordReset',
    async ({ email }: RequestPasswordResetPayload) => {
        const res: any = await authClient.post('auth/forgot-password', { email });
        return res;
    }
);

export const verifyResetPin = createAsyncThunkWrapper<VerifyResetPinResult, VerifyResetPinPayload>(
    'auth/verifyResetPin',
    async ({ email, pin }: VerifyResetPinPayload) => {
        const res = await authClient.post<VerifyResetPinResult>('auth/verify-reset-pin', { email, pin });
        return res;
    }
);

export const resetPassword = createAsyncThunkWrapper<any, ResetPasswordPayload>(
    'auth/resetPassword',
    async ({ resetToken, newPassword }: ResetPasswordPayload) => {
        const res: any = await authClient.post('auth/reset-password', { resetToken, newPassword });
        return res;
    }
);

export const changePassword = createAsyncThunkWrapper<any, ChangePasswordPayload>(
    'auth/changePassword',
    async ({ currentPassword, newPassword }: ChangePasswordPayload) => {
        const res: any = await authClient.post('auth/change-password', { currentPassword, newPassword });
        return res;
    }
);

export const verifyEmailPin = createAsyncThunkWrapper<any, VerifyEmailPinPayload>(
    'auth/verifyEmailPin',
    async ({ email, pin }: VerifyEmailPinPayload) => {
        const res: any = await authClient.post('auth/verify-email', { email, pin });
        return res;
    }
);

export const resendVerificationEmail = createAsyncThunkWrapper<any, ResendVerificationPayload>(
    'auth/resendVerificationEmail',
    async ({ email }: ResendVerificationPayload) => {
        const res: any = await authClient.post('auth/resend-verification', { email });
        return res;
    }
);

export const request2FAPin = createAsyncThunkWrapper<any, Request2FAPinPayload>(
    'auth/request2FAPin',
    async ({ pendingToken, method }: Request2FAPinPayload) => {
        const res: any = await authClient.post('auth/2fa/request-pin', { method }, { Authorization: `Bearer ${pendingToken}` });
        return res;
    }
);

export const verify2FAPin = createAsyncThunkWrapper<any, Verify2FAPayload>(
    'auth/verify2FAPin',
    async ({ pendingToken, pin, deviceId, deviceName }: Verify2FAPayload) => {
        const trustDevice = deviceId ? { deviceId, name: deviceName } : undefined;
        const response: any = await authClient.post('auth/2fa/verify', { pin, trustDevice }, { Authorization: `Bearer ${pendingToken}` });
        authClient.setToken(response.accessToken, response.refreshToken);
        localStorage.setItem('token', JSON.stringify(response));
        return response;
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateAuthInfo(state, action: PayloadAction<AuthData>) {
            const { payload } = action || {};
            if (!payload) return;
            state.data = payload;
        },
        resetState() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(login.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(login.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action;
            // A 2FA challenge or an email-not-verified signal isn't a real session yet -
            // only a payload carrying real tokens should populate auth state.
            if (!payload || payload.challenge || payload.emailNotVerified) return;
            state.data = payload;
        });
        builder.addCase(login.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
        builder.addCase(verify2FAPin.fulfilled, (state, action) => {
            if (!action.payload) return;
            state.data = action.payload;
        });
    },
});

export const { updateAuthInfo, resetState } = authSlice.actions;
export default authSlice.reducer;
